<template>
	<div class="row centered">
		<AppTable
			ref="zreports-table"
			v-if="userStore.currBusinessId"
			:fetch-function="epsonStore.getFilteredEpsonZReports"
			:filters="filters"
			:table-headings="tableHeadings"
			:title="getString('daily_closures')"
			:items="epsonStore.businessEpsonZReports || []"
			:total-items="epsonStore.businessEpsonZReportsTotal || 0"
			initial-sort-field="date"
			allow-download
			@export="exportReports"
			@failed-fetch="details=>{$emit('failed-fetch', details)}"
		>
			<template #actions="{item}">
				<div class="row centered">
					<AppTableButtonGroup
						:buttons="tableActionButtons(item)"
						:item="item"
						@picked-daily-closure-view-report="viewReport(item)"
						@picked-daily-closure-download="downloadReport(item)"
						@picked-daily-closure-retry-injest="retryReportInjest(item)"
					/>
				</div>
			</template>
		</AppTable>

		<AppBottomSheet
			ref="daily-closure-txt"
			:force-width="bottomSheetWidth"
		>
			<ModalHeader :title="getString('orders_daily_closing')" :button-text="getString('close')" @click-button="$refs['daily-closure-txt'].toggleStatus()" />
			<div class="col centered">
				<AppLoader v-if="isLoadingFile" />
				<div v-else-if="fileError" class="row centered pt-20 pb-20">
					<p>{{ getString('file_load_error') }}</p>
				</div>
				<pre v-else-if="fileContent" class="report-style">{{ fileContent }}</pre>
			</div>
		</AppBottomSheet>
	</div>
</template>

<script>
import AppTable from "@/shared/components/AppTable.vue";
import AppTableButtonGroup from "@/shared/components/AppTableButtonGroup.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import AppLoader from "@/shared/components/AppLoader.vue";
import {useAdministrationStore, useUserStore, useEpsonStore} from "@/console-new/stores";
import {useLanguageStore, useScriptStore, useCurrencyStore, useNotificationStore} from "@/shared/stores";
import {formatDateTimeWithSeconds} from "@/shared/helpers/dates";
import {exportToFile} from "@/shared/helpers/excel";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import {getNative, isNative} from "@/shared/helpers/webapp";

export default {
	name: "DailyClosuresTable",
	emits: ["failed-fetch"],
	components: {AppTable, AppTableButtonGroup, AppBottomSheet, ModalHeader, AppLoader},
	mixins:	[WindowWidth],
	setup() {
		const administrationStore = useAdministrationStore();
		const userStore = useUserStore();
		const languageStore = useLanguageStore();
		const scriptStore = useScriptStore();
		const currencyStore = useCurrencyStore();
		const notificationStore = useNotificationStore();
		const epsonStore = useEpsonStore();
		return {
			epsonStore,
			administrationStore,
			userStore,
			getString: languageStore.getString,
			scriptStore,
			currencyStore,
			showNotification: notificationStore.showNotification,
		};
	},
	data() {
		return {
			filters: [
				{
					type: "date",
					key: 'date',
					tableName: 'businesses_z_reports',
					text: this.getString('date'),
					finalValue: '',
					active: false
				},
			],
			tableHeadings: [
				{
					text: this.getString('z_report'),
					cols: 1,
					sortable: true,
					fieldName: 'z_rep',
				},
				{
					text: this.getString('fiscalPrinter'),
					cols: 1,
					sortable: true,
					fieldName: 'serial_number',
				},
				{
					text: this.getString('date'),
					cols: 1,
					sortable: true,
					fieldName: 'date',
					formatter: function(val) {
						return val instanceof Date ? formatDateTimeWithSeconds(val, false) : '-';
					}
				},
				{
					text: this.getString('total_gross'),
					cols: 1,
					sortable: true,
					fieldName: 'total_gross',
					formatter: 'currency',
				},
				{
					text: this.getString('vats'),
					cols: 1,
					sortable: true,
					fieldName: 'total_vat',
					formatter: 'currency',
				},
				{
					text: this.getString('total_refund'),
					cols: 1,
					sortable: true,
					fieldName: 'total_refund',
					formatter: 'currency',
				},
				{
					text: this.getString('total_cancel'),
					cols: 1,
					sortable: true,
					fieldName: 'total_cancel',
					formatter: 'currency',
				},
				{
					text: this.getString('actions'),
					cols: 1,
					fieldName: 'actions',
					slotName: 'actions',
				},
			],
			fileContent: '',
			isLoadingFile: false,
			fileError: null
		};
	},
	computed: {
		bottomSheetWidth() {
			if (this.windowWidth < 550) {
				return "100%";
			} else {
				return "550px";
			}
		},
	},
	methods: {
		exportReports(expExtension) {
			const reports = this.epsonStore.businessEpsonZReports || [];
			const exportableHeadings = this.tableHeadings.filter(heading =>
				heading.text && !heading.slotName
			);

			const columns = exportableHeadings.map(heading => {
				if (heading.formatter === 'currency') {
					return {
						header: heading.text,
						value: r => this.currencyStore.dbToActualValue(r[heading.fieldName]),
						type: 'number', numberFormat: '#,##0.00'
					};
				}
				if (heading.formatter && typeof heading.formatter === 'function') {
					return { header: heading.text, value: r => heading.formatter(r[heading.fieldName]) };
				}
				return {
					header: heading.text,
					value: r => r[heading.fieldName] === undefined || r[heading.fieldName] === null ? '-' : r[heading.fieldName]
				};
			});

			const today = new Date().toLocaleString();
			const businessName = this.userStore.currBusiness ? this.userStore.currBusiness.name : 'business';
			const businessNameNorm = businessName.toLowerCase().replace(/[^a-zA-Z0-9]/g, "");
			const fileName = businessNameNorm+"_"+this.getString('daily_closures').toLowerCase()+"_"+today.replace(", ", "_");
			exportToFile({
				columns,
				rows: reports,
				fileName,
				sheetName: this.getString('daily_closures'),
				format: expExtension,
				onEmpty: () => { if(this.showNotification) this.showNotification('table-no-data'); },
			});
		},
		tableActionButtons(item) {
			let actions = [];

			if (item.result === "success" && item.txt_file_s3_url) {
				actions.push({
					emitName: 'picked-daily-closure-view-report',
					iconName: 'icon-eye-blue',
					title: this.getString('view'),
				});
				actions.push({
					emitName: 'picked-daily-closure-download',
					iconName: 'icon-download-blue',
					title: this.getString('download'),
				});
			}

			if (['zrep-xml-fetch-failure', 'zrep-txt-fetch-failure', 'baseurl-failure'].includes(item.result) && isNative()) {
				actions.push({
					emitName: 'picked-daily-closure-retry-injest',
					iconName: 'icon-redo-blue',
					title: this.getString('retry'),
				});
			}

			return actions;
		},
		viewReport(item) {
			if (!item.txt_file_s3_url) {
				this.showNotification('generic-error');
				return;
			}

			this.$refs['daily-closure-txt'].toggleStatus();
			this.isLoadingFile = true;
			this.fileError = null;
			this.fileContent = '';

			fetch(item.txt_file_s3_url)
				.then(response => {
					if (!response.ok) {
						this.fileError = `HTTP ${response.status}: ${response.statusText}`;
						this.isLoadingFile = false;
						return;
					}
					return response.text();
				})
				.then(text => {
					if (text) {
						this.fileContent = text;
					}
				})
				.catch(error => {
					this.fileError = error.message;
					console.error('Error fetching file:', error);
				})
				.finally(() => {
					this.isLoadingFile = false;
				});
		},
		downloadReport(item) {
			if (!item.txt_file_s3_url) {
				this.showNotification('generic-error');
				return;
			}

			fetch(item.txt_file_s3_url)
				.then(response => {
					if (!response.ok) {
						throw new Error(`HTTP ${response.status}: ${response.statusText}`);
					}
					return response.text();
				})
				.then(fileContent => {
					const blob = new Blob([fileContent], { type: 'text/plain' });
					const url = URL.createObjectURL(blob);

					const link = document.createElement('a');
					link.href = url;
					link.download = `z-report-${item.z_rep || 'report'}-${new Date().toISOString().split('T')[0]}.txt`;
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);

					URL.revokeObjectURL(url);
				})
				.catch(error => {
					console.error('Error downloading file:', error);
					this.showNotification('generic-error');
				});
		},
		retryReportInjest(item) {

			if (item.result === "baseurl-failure"){
				this.epsonStore.zreportToReinjest = {...item, retrySucceded: false};
				getNative().zReportFileMatch(item.base_url, item.z_rep, item.serial_number)
			}
			else if (item.result === "zrep-txt-fetch-failure" || item.result === "zrep-xml-fetch-failure") {
				this.epsonStore.zreportToReinjest = {...item, retrySucceded: false};
				getNative().fetchZReportFiles(item.base_url, item.xml_file, item.txt_file, item.z_rep, item.serial_number);
			}

		}
	},
	watch: {
		'userStore.currBusinessId'(newVal, oldVal) {
			if (newVal && newVal !== oldVal) {
				if (this.$refs['zreports-table']) {
					this.$refs['zreports-table'].fetchData();
				}
			}
		},
		'epsonStore.zreportToReinjest': {
			handler(newVal, oldVal) {
				if(newVal && newVal.hasOwnProperty("retrySucceded") && newVal.retrySucceded) {
					if(this.$refs['zreports-table']){
						this.$refs['zreports-table'].fetchData();
						this.epsonStore.zreportToReinjest = null;
					}
				}
			},
			deep: true
		}
	}
};
</script>

<style scoped>
.report-style {
	text-align: center;
	font-family: monospace;
	white-space: pre-wrap;
	word-wrap: break-word;
	max-width: 100%;
	overflow-x: auto;

	@media (max-width: 444px) {
		font-size: 9px;

	}
}
</style>